//#region Styles
import {
    Row, 
    Column, 
    Part, 
    TitleSection, 
    Fields, 
    HelperText
} from "components/styles";
//#endregion
//#region Components
import HandlerRadio from "components/CustomRadio/CustomRadio";
import NotificationNoContent from "components/NotificationNoContent/NotificationNoContent";
//#endregion
//#region Utils
import { VIRTUAL_CAMPUS_ID } from "config/constants";
import { filterCampusVirtual } from "services/general";
import ZoomControls from "./components/ZoomControls/ZoomControls";
import PlaceControls from "./components/PlaceControls/PlaceControls";
import { useCallback, useEffect, useMemo, useRef } from "react";
//#endregion
import { useLocation } from 'react-router-dom'

const DEFAULT_PUBLIC_LOAD = {};
const DEFAULT_PUBLIC_LOAD_STUDENTS = {
    publicByCampusIds: [],
    modalityId: 0
};

const LocationPartForm = ({
    form, formPart2={}, changeField, 
    changeFieldPart2=() => {},
    errors, errorTexts, 
    locations, allCampus = [], campus = [],
    inscriptionTypes, 
    campusEnvironments,
    teacherZoomAccountDetail, 
    fillTeacherZoomAccountDetail, as,
    onlyInCampus = false,
    isAsesoria
}) => {
    //#region Effects
    //#endregion
    //#region Functions
    const lastSyncedCampusRef = useRef({
        id: 0,
        name: "",
        allCampusLen: 0,
        campusLen: 0
    });

    const getCampusDetailById = detailId => {
        return allCampus.find(({ id }) => id === detailId) || null;
    };

    const getCampusSummaryByDetailId = detailId => {
        const selectedDetail = getCampusDetailById(detailId);
        const campusId = selectedDetail?.campusId || selectedDetail?.id;
        return campus.find(({ id }) => id === campusId) || selectedDetail || null;
    };

    const isVirtualCampusByDetailId = detailId => {
        return getCampusSummaryByDetailId(detailId)?.code === VIRTUAL_CAMPUS_ID;
    };

    const handlerLocationIdField = (value) => {
        changeField("locationId", value, false);
        fillTeacherZoomAccountDetail && fillTeacherZoomAccountDetail({
            locationId: value
        });
    }
    const changePlace = (field, value, errorValue) => {
        changeField("place", {
            ...form.place, 
            [field]: value
        }, {
            ...errors.place,
            [field]: errorValue
        });
    }
    const changePlaceCampus = ({ id, text }) => {
        changeField("place", {
            "campus": { id, name: text },
            "environment": {
                ...form.place.environment, 
                inCampus: {
                    id: 0, 
                    address: "",
                    floor: ""
                }
            }
        }, {
            ...errors.place,
            "campus": false,
            "environment": errors.place.environment
        });
    }
    const changePlaceCampusOnlyInCampus = useCallback(({ 
        id, 
        text: name 
    }) => {
        const selectedDetail = allCampus.find(({ id: detailId }) => detailId === id) || null;
        const campusId = selectedDetail?.campusId || selectedDetail?.id;
        const selectedSummary = campus.find(({ id: summaryId }) => summaryId === campusId) || selectedDetail || null;
        const isVirtualCampus = selectedSummary?.code === VIRTUAL_CAMPUS_ID;
        const environment = {
            ...form.place.environment,
            address: selectedDetail?.address || "-",
            ...(isVirtualCampus
                ? { envName: selectedSummary?.name || "Virtual", floor: "-" }
                : { envName: "", floor: "" })
        };
        const hasCampusChanged = form.place.campus.id !== id || form.place.campus.name !== name;
        const hasEnvironmentChanged =
            form.place.environment?.envName !== environment?.envName ||
            form.place.environment?.floor !== environment?.floor ||
            form.place.environment?.address !== environment?.address;

        if (!hasCampusChanged && !hasEnvironmentChanged) return;

        changeField("place", {
            "campus": { id, name },
            "environment": environment
        }, {
            ...errors.place,
            "campus": false,
            "environment": errors.place.environment
        });
    }, [changeField, errors.place, form.place, allCampus, campus]);
    const changePlaceEnvironment = (field, value, errorValue) => {
        changePlace("environment", {
            ...form.place.environment, 
            [field]: value 
        }, {
            ...errors.place.environment,
            [field]: errorValue
        });
    }
    const getAllCampusForLocationSearch = () => {
        if (isAsesoria) return allCampus;
        const hasCampusDetailRelation = allCampus.some(row => row?.campusId !== undefined);
        if (!hasCampusDetailRelation) return filterCampusVirtual(allCampus);
        // filter out virtual campuses by matching detail.campusId -> campus.id
        return allCampus.filter(({ id }) => !isVirtualCampusByDetailId(id));
    }

    const publicLoad = useMemo(() => {
        return formPart2?.publicLoad || DEFAULT_PUBLIC_LOAD;
    }, [formPart2?.publicLoad]);

    const publicLoadStudents = useMemo(() => {
        return publicLoad?.students || DEFAULT_PUBLIC_LOAD_STUDENTS;
    }, [publicLoad?.students]);

    const changePublicLoad = useCallback((field, value, errorValue) => {
        changeFieldPart2("publicLoad", {
            ...publicLoad,
            [field]: value
        }, false);
    }, [changeFieldPart2, publicLoad]);

    const changePublicLoadStudentsBatch = useCallback((fields, errorsFields = {}) => {
        changePublicLoad(
            "students",
            {
                ...publicLoadStudents,
                ...fields,
            },
            false
        );
    }, [changePublicLoad, publicLoadStudents]);

    useEffect(() => {
        if (!isAsesoria) return;

        const desiredCampusIds = campus.map(row => row.id);
        const currentCampusIds = publicLoadStudents.publicByCampusIds || [];
        const currentModalityId = publicLoadStudents.modalityId || 0;

        const hasSameCampusIds =
            currentCampusIds.length === desiredCampusIds.length &&
            currentCampusIds.every((id, index) => id === desiredCampusIds[index]);

        if (form.locationId !== 2) {
            changeField("locationId", 2, false);
        }

        if (!hasSameCampusIds || currentModalityId !== 3) {
            changePublicLoadStudentsBatch(
                {
                    publicByCampusIds: desiredCampusIds,
                    modalityId: 3
                }
            );
        }
    }, [
        isAsesoria,
        campus,
        form.locationId,
        publicLoadStudents,
        changeField,
        changePublicLoadStudentsBatch
    ]);

    useEffect(() => {
        if (!onlyInCampus) return;
        const selectedCampusDetailId = form.place?.campus?.id;
        const selectedCampusName = form.place?.campus?.name || "";
        if (!selectedCampusDetailId) return;

        const syncSignature = {
            id: selectedCampusDetailId,
            name: selectedCampusName,
            allCampusLen: allCampus.length,
            campusLen: campus.length
        };

        const hasSameSignature =
            lastSyncedCampusRef.current.id === syncSignature.id &&
            lastSyncedCampusRef.current.name === syncSignature.name &&
            lastSyncedCampusRef.current.allCampusLen === syncSignature.allCampusLen &&
            lastSyncedCampusRef.current.campusLen === syncSignature.campusLen;

        if (hasSameSignature) return;
        lastSyncedCampusRef.current = syncSignature;

        changePlaceCampusOnlyInCampus({
            id: selectedCampusDetailId,
            text: selectedCampusName
        });
    }, [
        onlyInCampus,
        form.place?.campus?.id,
        form.place?.campus?.name,
        allCampus.length,
        campus.length,
        changePlaceCampusOnlyInCampus
    ]);

    //#endregion
    const { locationId, place } = form;
    const location = useLocation()
    const isEmployability = useMemo(() => {
        return location.pathname.includes("/asesorias/empleabilidad/crear");
    }, [location?.pathname])
    const campusText = useMemo(() => {
        if (isEmployability) {
            return `*El campus seleccionado en el Paso 01 indica las programaciones presenciales que estarán disponibles para los alumnos de ese campus. De igual forma, la programación del asesor se verá para los demás campus de manera virtual.`
        }
        return `*El campus seleccionado en el Paso 01 indica las programaciones presenciales que estarán disponibles para los alumnos de ese campus. De igual manera la programación del ${isAsesoria ? "asesor" : "psicólogo"} se verá para todos los otros campus de manera virtual.`
    }, [isAsesoria, isEmployability])
    return (
        <Part>
            <Fields>
                <Column gap="20px">
                    <TitleSection>{isAsesoria? "Ubicación y público:" : "Ubicación:"}</TitleSection>
                    {
                        !isAsesoria &&
                        <Row className="flex-start" gap="40px">
                            <HandlerRadio
                                value={locationId}
                                onChange={handlerLocationIdField}
                                radiosProps={
                                    locations.map(({ 
                                            id, 
                                            name,
                                            icon 
                                        }) => ({
                                        value: id, 
                                        label: name,
                                        icon: <img src={icon} alt={name}/>
                                    }))
                                }
                                error={errors.locationId}
                                errorText={errorTexts?.location || "Es necesario seleccionar una ubicación."}/>
                        </Row>
                    }
                    {locationId === 1 
                    ? <ZoomControls 
                        as={as}
                        data={{
                            inscriptionTypes
                        }}
                        form={form} changeField={changeField}
                        errors={errors} errorTexts={errorTexts}
                        teacherZoomAccountDetail={teacherZoomAccountDetail}/>
                    : (locationId === 2 || isAsesoria) 
                        ? <>
                        <PlaceControls 
                            as={as}
                            data={{
                                campusEnvironments,
                                fn: { getAllCampusForLocationSearch },
                                campusList: campus,
                                campusDetailList: allCampus
                            }}
                            form={place}
                            errors={errors}
                            changeFields={{
                                changePlaceCampus,
                                changePlaceCampusOnlyInCampus,
                                changePlaceEnvironment
                            }}
                            onlyInCampus={onlyInCampus}/>
                            <HelperText>
                                {campusText}
                            </HelperText>
                        </>
                        : <NotificationNoContent
                            title="Aún no has señalado una ubicación"
                            description={`Selecciona una para mostrarte las opciones de cómo asistir al ${as}`}/> }
                </Column>
            </Fields>
        </Part>
    );
}

export default LocationPartForm;
